/**
 * The meeting, framed by another application.
 *
 * Everything that differs when `/meeting/:id` runs inside someone else's page
 * goes through here, and NOTHING here runs for a top-level page: `isEmbedded()`
 * is false and every function is a no-op, so the direct-browser app is
 * untouched.
 *
 * WHO MAY FRAME US. `TRUSTED_EMBED_ORIGINS` on the backend, published at
 * `/api/v1/embed/config`. The browser enforces it through the
 * `frame-ancestors` header on our HTML; this module checks it again because a
 * missing header is a deployment mistake that should not silently turn into
 * "any site may host the meeting and talk to it". With no list configured the
 * frame behaves exactly as it always has — a guest join, no messaging.
 *
 * TALKING TO THE PARENT. A small postMessage protocol, origin-locked both ways:
 * we address only the parent's verified origin (never "*"), and accept only
 * messages whose `source` is our own parent window AND whose origin is on the
 * list. The parent can supply a SecureID token and ask us to leave or mute; it can
 * never reach anything else.
 *
 *   to parent    { source: 'inmeet', version: 1, type, payload }
 *                ready | auth-request | joined | left | meeting-ended |
 *                connection-state-changed | error | closed
 *   from parent  { source: 'inmeet-host', type, payload }
 *                auth { secureIdToken: string | null } | leave | mute
 *
 * There is deliberately no `unmute`: whether this participant may speak is
 * the host's call (host controls, view-only links), checked inside the meeting
 * UI. Muting is always safe; un-muting on a host page's say-so is not.
 */
import { config } from '@/config/env'

export type EmbedEvent =
  | 'ready'
  | 'auth-request'
  | 'joined'
  | 'left'
  | 'meeting-ended'
  | 'connection-state-changed'
  | 'error'
  | 'closed'

export type EmbedCommand = 'auth' | 'leave' | 'mute'

/**
 * What a host page must grant the frame for the whole meeting to work: camera
 * and mic, screen sharing, fullscreen, copying the invite, playing join sounds,
 * picture-in-picture, and keeping the screen on. Missing any one of them fails
 * that feature quietly inside the frame — without `screen-wake-lock` a phone
 * locks mid-meeting whenever the camera is off.
 */
export const EMBED_IFRAME_ALLOW =
  'camera; microphone; display-capture; fullscreen; clipboard-write; autoplay; picture-in-picture; screen-wake-lock'

const COMMANDS: ReadonlySet<string> = new Set<EmbedCommand>(['auth', 'leave', 'mute'])

/**
 * `off`      — top level, or framed with no allowlist configured (legacy).
 * `blocked`  — framed by a site that is not on the allowlist.
 * `active`   — framed by a trusted site; messaging is on.
 */
export type EmbedState = 'off' | 'blocked' | 'active'

type Handler = (payload: Record<string, unknown>) => void

let state: EmbedState = 'off'
let trusted: string[] = []
/** The parent's origin once verified — by the browser's ancestor list, or by its first message. */
let parentOrigin: string | null = null
let initPromise: Promise<EmbedState> | null = null
const handlers = new Map<EmbedCommand, Set<Handler>>()

/** True inside any frame. A cross-origin `window.top` read can throw, which also means framed. */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/**
 * The embedding page's origin, when the browser will tell us.
 *
 * `ancestorOrigins` (Chromium, Safari) is browser-supplied and cannot be forged
 * by the parent. Firefox lacks it, so the referrer is the fallback — present
 * unless the parent sends `Referrer-Policy: no-referrer`, in which case the
 * parent's first message establishes it instead (see `onMessage`).
 */
function knownAncestorOrigin(): string | null {
  const ancestors = (window.location as Location & { ancestorOrigins?: DOMStringList }).ancestorOrigins
  if (ancestors && ancestors.length > 0) return ancestors[0]
  try {
    return document.referrer ? new URL(document.referrer).origin : null
  } catch {
    return null
  }
}

function onMessage(e: MessageEvent): void {
  if (e.source !== window.parent) return
  if (!trusted.includes(e.origin)) return
  if (parentOrigin && e.origin !== parentOrigin) return

  const data = e.data as { source?: unknown; type?: unknown; payload?: unknown } | null
  if (!data || typeof data !== 'object' || data.source !== 'inmeet-host') return
  if (typeof data.type !== 'string' || !COMMANDS.has(data.type)) return

  parentOrigin = e.origin
  const payload =
    data.payload && typeof data.payload === 'object' ? (data.payload as Record<string, unknown>) : {}
  for (const h of handlers.get(data.type as EmbedCommand) ?? []) h(payload)
}

/**
 * Work out whether, and with whom, we may talk. Once per page; safe to await
 * from several places.
 */
export function initEmbedBridge(): Promise<EmbedState> {
  if (initPromise) return initPromise
  initPromise = (async () => {
    if (!isEmbedded()) return (state = 'off')

    let configLoaded = false
    try {
      // A plain GET on purpose: any custom header would need a CORS preflight
      // the backend does not allow (server.ts allowedHeaders).
      const res = await fetch(`${config.apiUrl}/api/v1/embed/config`)
      const body = (await res.json()) as { trustedOrigins?: unknown }
      if (Array.isArray(body.trustedOrigins)) {
        trusted = body.trustedOrigins.filter((o): o is string => typeof o === 'string')
        configLoaded = true
      }
    } catch {
      /* configLoaded stays false */
    }

    // Could not read the list. Refuse rather than fall back to "unmanaged": a
    // failed lookup must never turn into "any site may host the meeting".
    if (!configLoaded) {
      console.warn('[embed] could not read TRUSTED_EMBED_ORIGINS; refusing to run embedded')
      return (state = 'blocked')
    }

    // No list configured: embedding is unmanaged, as it was before this module existed.
    if (trusted.length === 0) return (state = 'off')

    const known = knownAncestorOrigin()
    if (known && !trusted.includes(known)) {
      console.warn(`[embed] ${known} is not in TRUSTED_EMBED_ORIGINS; refusing to run embedded`)
      return (state = 'blocked')
    }
    if (known) parentOrigin = known

    window.addEventListener('message', onMessage)
    state = 'active'
    emitToParent('ready')
    return state
  })()
  return initPromise
}

/** Tell the parent something happened. No-op unless the bridge is active. */
export function emitToParent(type: EmbedEvent, payload: Record<string, unknown> = {}): void {
  if (state !== 'active') return
  const message = { source: 'inmeet', version: 1, type, payload }
  if (parentOrigin) {
    window.parent.postMessage(message, parentOrigin)
    return
  }
  // Parent origin not yet known (Firefox with no referrer): address each
  // trusted origin in turn. The browser delivers only the one that matches the
  // parent, so nothing reaches a page that is not on the list.
  for (const origin of trusted) window.parent.postMessage(message, origin)
}

/** Subscribe to a parent command. Returns the unsubscribe. */
export function onParentCommand(type: EmbedCommand, handler: Handler): () => void {
  let set = handlers.get(type)
  if (!set) handlers.set(type, (set = new Set()))
  set.add(handler)
  return () => set!.delete(handler)
}

/**
 * Ask the parent for its user's SecureID access token.
 *
 * Resolves with the token, `null` when the parent says "no signed-in user"
 * (join as a guest), or `undefined` when it does not answer in time — a parent
 * that frames us without implementing the protocol.
 */
export function requestSecureIdToken(timeoutMs: number): Promise<string | null | undefined> {
  if (state !== 'active') return Promise.resolve(undefined)
  return new Promise((resolve) => {
    let off = () => {}
    const timer = window.setTimeout(() => {
      off()
      resolve(undefined)
    }, timeoutMs)
    off = onParentCommand('auth', (payload) => {
      window.clearTimeout(timer)
      off()
      const token = payload.secureIdToken
      resolve(typeof token === 'string' && token ? token : null)
    })
    emitToParent('auth-request')
  })
}
